/**
 * mobileCommandMatcher — pure matching logic for the mobile Copilot command
 * bar (UPT-1616, option B). Maps a free-text command to one of the visible
 * form actions.
 *
 * Deliberately deterministic (no backend call): multilingual synonym table
 * (en/fr/de/it/ar) + substring fallback on the button id/name. An AI intent
 * endpoint can replace `matchCommand` later without touching the UI.
 */

export interface CommandTarget {
  /** Stable key (button map key or id). */
  key: string;
  /** Human label shown in confirmation feedback. */
  label: string;
  /** Lowercased haystack: button id + name + map key. */
  haystack: string;
}

/**
 * Canonical command → synonyms (all lowercase, accent-tolerant matching is
 * done by normalising both sides with NFD stripping).
 */
const COMMAND_SYNONYMS: ReadonlyArray<readonly string[]> = [
  // save / submit
  ['save', 'submit', 'sauvegarder', 'enregistrer', 'enregistre', 'speichern', 'salvare', 'salva', 'حفظ'],
  // cancel / back
  ['cancel', 'back', 'annuler', 'annule', 'retour', 'zurück', 'abbrechen', 'annulla', 'indietro', 'إلغاء', 'رجوع'],
  // delete
  ['delete', 'remove', 'supprimer', 'supprime', 'effacer', 'löschen', 'entfernen', 'elimina', 'cancella', 'حذف'],
  // validate / approve
  ['validate', 'approve', 'valider', 'valide', 'approuver', 'validieren', 'genehmigen', 'convalida', 'approva', 'تحقق', 'اعتماد'],
  // export
  ['export', 'exporter', 'exportieren', 'esporta', 'تصدير'],
  // print
  ['print', 'imprimer', 'imprime', 'drucken', 'stampa', 'طباعة'],
  // new / create
  ['new', 'create', 'add', 'nouveau', 'nouvelle', 'créer', 'cree', 'ajouter', 'neu', 'erstellen', 'nuovo', 'crea', 'جديد', 'إنشاء'],
  // duplicate
  ['duplicate', 'copy', 'dupliquer', 'duplique', 'copier', 'duplizieren', 'kopieren', 'duplica', 'copia', 'نسخ'],
];

/** Strip accents/diacritics and lowercase for tolerant matching. */
export const normalizeCommand = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/**
 * Match a free-text command against the visible form actions.
 *
 * Strategy (first hit wins):
 *   1. exact synonym → target whose haystack contains any sibling synonym
 *      (e.g. "sauvegarder" → button whose name contains "save"/"enregistrer"…)
 *   2. direct substring of the normalized command in the target haystack
 *      (covers custom buttons like "Create Budget")
 *
 * Returns the matched target, or null when nothing matches.
 */
export function matchCommand(
  rawCommand: string,
  targets: ReadonlyArray<CommandTarget>,
): CommandTarget | null {
  const command = normalizeCommand(rawCommand);
  if (!command || targets.length === 0) return null;

  // 1. Synonym-group match
  for (const group of COMMAND_SYNONYMS) {
    const normalizedGroup = group.map(normalizeCommand);
    if (!normalizedGroup.some((syn) => command.includes(syn))) continue;

    for (const target of targets) {
      if (normalizedGroup.some((syn) => target.haystack.includes(syn))) {
        return target;
      }
    }
  }

  // 2. Direct substring fallback (e.g. "create budget" hits "Create Budget")
  for (const target of targets) {
    if (target.haystack.includes(command)) return target;
  }

  return null;
}
